import React, { useEffect, useRef, useState } from 'react';
import { AuthUser } from '../services/AuthService';
import { TwinContextService, TwinFrame } from '../services/TwinContextService';
import { EnergyPlusTestbedService, type EnergyPlusTestbedStatus } from '../services/EnergyPlusTestbedService';
import type { DataSourceMode } from '../services/DataBridge';
import { ControlMode } from '../types';
import { DataSourceModeSelector } from './DataSourceModeSelector';

interface HeaderProps {
  activeTab: string;
  user: AuthUser | null;
  controlMode: ControlMode;
  onModeChange: (mode: ControlMode) => void;
  dataSourceMode: DataSourceMode;
  onDataSourceModeChange: (mode: DataSourceMode) => void | Promise<void>;
  onLogout: () => void;
}

interface TrackingSetpoints {
  ahu_sa_temp?: number | null;
  zone_cooling_sp?: number | null;
  chw_sp?: number | null;
  cw_sp?: number | null;
  bt4_sp?: number | null;
  buffer_tank_sp?: number | null;
}

interface TrackingStatusPayload {
  setpoints?: TrackingSetpoints;
  setpoint_status?: Record<string, {
    source?: string | null;
    applied?: boolean | null;
  }>;
  energyplus_batch_status?: {
    running?: boolean;
  };
}

const TAB_TITLES: Record<string, string> = {
  overview: '공간 디지털트윈',
  'floor-1f': '1층 운영 분석',
  'floor-2f': '2층 운영 분석',
  'floor-3f': '3층 운영 분석',
  'loop-water': '열원 시스템',
  'loop-air': '공조 시스템',
  'ai-control': 'AI 제어 분석',
  'ai-explain': '제어 근거 분석',
  'ai-model': '정책 튜닝',
  'digital-twin': '트윈 분석',
  reporting: '운영 리포트',
  settings: '환경 설정',
};

const sourceText = (frame: TwinFrame) => {
  if (frame.source === 'historical-playback') return `BEMS 실측 재생 · ${frame.pointCount}개 포인트`;
  if (frame.source === 'historical-default') return `BEMS 실측 스냅샷 · ${frame.pointCount}개 포인트`;
  if (frame.source === 'live-measured') return `실시간 BEMS · ${frame.pointCount}개 포인트`;
  if (frame.source === 'energyplus') return `EnergyPlus 가상 BEMS · ${frame.pointCount}개 포인트`;
  return '데이터 연결 확인 필요';
};

const formatSetpoint = (value: number | null | undefined, offAt?: number) => {
  if (!Number.isFinite(value)) return '--';
  const numeric = Number(value);
  if (offAt != null && numeric >= offAt) return 'OFF';
  return `${numeric.toFixed(2)}°C`;
};

const formatChillerStatus = (chwSetpoint: number | null | undefined) => {
  if (!Number.isFinite(chwSetpoint)) return '--';
  return Number(chwSetpoint) >= 29 ? 'OFF' : 'ON';
};

const controlFrameTime = (frame: TwinFrame, fallback: Date) => {
  if (frame.date && frame.time) return `${frame.date} ${frame.time.slice(0, 5)}`;
  return fallback.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
};

export const ControlSetpointStrip: React.FC<{ className?: string }> = ({ className = '' }) => {
  const [time, setTime] = useState(new Date());
  const [twinFrame, setTwinFrame] = useState<TwinFrame>(() => TwinContextService.getFrame());
  const [trackingStatus, setTrackingStatus] = useState<TrackingStatusPayload | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => setTime(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => TwinContextService.subscribe(setTwinFrame), []);

  useEffect(() => {
    let cancelled = false;
    const fetchTrackingStatus = async () => {
      try {
        const response = await fetch('/api/twin/tracking/status', { cache: 'no-store' });
        if (!response.ok) throw new Error('Tracking status unavailable');
        const payload = await response.json();
        if (!cancelled) setTrackingStatus(payload);
      } catch {
        if (!cancelled) setTrackingStatus(null);
      }
    };
    fetchTrackingStatus();
    const timer = window.setInterval(fetchTrackingStatus, 15_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const setpointTime = controlFrameTime(twinFrame, time);
  const currentSetpoints = trackingStatus?.setpoints ?? {};
  const controlGroups = [
    {
      label: '공조',
      items: [
        { label: 'AHU 급기', value: formatSetpoint(currentSetpoints.ahu_sa_temp) },
      ],
    },
    {
      label: '열원',
      items: [
        { label: '냉동기', value: formatChillerStatus(currentSetpoints.chw_sp) },
        { label: '냉수 온도', value: formatSetpoint(currentSetpoints.chw_sp) },
        { label: '냉각수', value: formatSetpoint(currentSetpoints.cw_sp) },
      ],
    },
    {
      label: '축열',
      items: [
        { label: 'BT4', value: formatSetpoint(currentSetpoints.bt4_sp) },
        { label: 'BT5', value: formatSetpoint(currentSetpoints.buffer_tank_sp) },
      ],
    },
  ];

  return (
    <div className={`min-w-0 items-center gap-4 border-l border-[#dfe3e6] pl-4 ${className}`}>
      <div className="whitespace-nowrap text-right">
        <p className="text-[10px] font-semibold tracking-[0.12em] text-[#70767d]">현재 제어 변수 적용값</p>
        <p className="mt-0.5 text-sm font-bold text-[#08777d]">
          {trackingStatus?.energyplus_batch_status?.running ? '후보 계산 중' : setpointTime}
        </p>
      </div>
      <div className="flex min-w-0 items-center gap-4">
        {controlGroups.map((group, groupIndex) => (
          <div
            key={group.label}
            className={`flex items-center gap-2 ${groupIndex > 0 ? 'border-l border-[#e1e4e7] pl-4' : ''}`}
          >
            <p className="text-[9px] font-semibold tracking-[0.12em] text-[#8a9097]">{group.label}</p>
            <div className="flex items-center gap-2.5">
              {group.items.map(item => (
                <div key={`${group.label}-${item.label}`} className="min-w-[48px] text-right">
                  <p className="whitespace-nowrap text-[9px] font-semibold text-[#7a848c]">{item.label}</p>
                  <p className="mt-0.5 whitespace-nowrap text-xs font-bold tracking-[-0.01em] text-[#171a20]">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Header: React.FC<HeaderProps> = ({
  activeTab,
  user,
  controlMode,
  onModeChange,
  dataSourceMode,
  onDataSourceModeChange,
  onLogout,
}) => {
  const [time, setTime] = useState(new Date());
  const [twinFrame, setTwinFrame] = useState<TwinFrame>(() => TwinContextService.getFrame());
  const [energyPlusStatus, setEnergyPlusStatus] = useState<EnergyPlusTestbedStatus>(() => EnergyPlusTestbedService.getStatus());
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = window.setInterval(() => setTime(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => TwinContextService.subscribe(setTwinFrame), []);
  useEffect(() => EnergyPlusTestbedService.subscribe(setEnergyPlusStatus), []);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
        setConfirming(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogoutClick = () => {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setMenuOpen(false);
    setConfirming(false);
    onLogout();
  };

  const roleLabel = user?.role === 'admin' ? '시스템 관리자' : '운영자';
  const roleColor = user?.role === 'admin' ? 'text-[#08777d]' : 'text-[#7050a5]';
  const roleIcon = user?.role === 'admin' ? 'fa-shield-halved' : 'fa-user-gear';
  const title = TAB_TITLES[activeTab] ?? activeTab.replace('-', ' ');
  const isEnergyPlusClosedLoop = twinFrame.source === 'energyplus';
  const energyPlusPolicyActive = Boolean(energyPlusStatus.running && energyPlusStatus.control?.policy_active);
  const energyPlusControlDetail = !energyPlusStatus.running
    ? '세션 정지'
    : energyPlusPolicyActive ? 'AI 정책 적용 · 20분 주기' : '운전시간 외';
  const autoControlLocked = !['live-measured', 'energyplus'].includes(twinFrame.source);
  const controlModes = [
    {
      id: ControlMode.DECISION_SUPPORT,
      label: '제안',
      fullLabel: '의사결정지원',
      icon: 'fa-lightbulb',
      title: 'AI 추천은 운영자 승인 전까지 적용되지 않습니다. 시연에서는 다음 제어 스텝 전까지, 현장에서는 해당 제어주기 안에 승인해야 합니다.',
      disabled: false,
    },
    {
      id: ControlMode.AI_AUTO,
      label: '자동',
      fullLabel: '자동제어',
      icon: 'fa-robot',
      title: autoControlLocked
        ? '실시간 BEMS 연결과 쓰기 권한 확인 후 활성화합니다.'
        : isEnergyPlusClosedLoop
          ? 'MASAC 제어값을 EnergyPlus 다음 20분 스텝에 자동 적용합니다.'
          : '검증을 통과한 정책만 자동 적용 대상으로 사용합니다.',
      disabled: autoControlLocked,
    },
    {
      id: ControlMode.MANUAL,
      label: '수동',
      fullLabel: '수동제어',
      icon: 'fa-hand-pointer',
      title: 'AI는 모니터링과 추천만 제공하고 실제 입력은 운영자가 수행합니다.',
      disabled: false,
    },
  ];
  const renderControlModeButtons = () => {
    return (
      <div className="flex rounded-xl border border-[#dfe3e6] bg-[#f7f8f8] p-1">
      {controlModes.map(mode => {
        const active = controlMode === mode.id;
        return (
          <button
            key={mode.id}
            type="button"
            title={mode.title}
            aria-label={mode.fullLabel}
            disabled={mode.disabled}
            onClick={() => !mode.disabled && onModeChange(mode.id)}
            className={`flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[11px] font-semibold transition ${
              active
                ? 'bg-[#171a20] text-white shadow-sm'
                : 'text-[#70767d] hover:bg-white hover:text-[#171a20]'
            } disabled:cursor-not-allowed disabled:opacity-35`}
          >
            <i className={`fa-solid ${mode.icon} text-[10px]`} />
            {mode.label}
          </button>
        );
      })}
      </div>
    );
  };

  return (
    <header className="relative z-40 flex min-h-[72px] flex-shrink-0 items-center justify-between gap-4 border-b border-[#e1e4e7] bg-white/95 px-4 backdrop-blur-xl lg:px-7">
      <div className="min-w-0 shrink-0">
        <div className="flex min-w-0 items-center gap-2.5">
          <h2 className="shrink-0 whitespace-nowrap text-base font-semibold text-[#171a20]">{title}</h2>
          <DataSourceModeSelector
            mode={dataSourceMode}
            onChange={onDataSourceModeChange}
            className="hidden md:flex"
          />
        </div>
        <div className="mt-2 flex items-center gap-2 lg:hidden">
          <span className="text-[10px] font-semibold text-[#777d85]">{isEnergyPlusClosedLoop ? '테스트 제어' : '운영 모드'}</span>
          {renderControlModeButtons()}
          {controlMode === ControlMode.DECISION_SUPPORT && (
            <span className="hidden text-[10px] font-semibold text-[#08777d] sm:inline">미응답 시 다음 스텝에 기존값 유지</span>
          )}
        </div>
      </div>

      <div className="flex min-w-0 items-center gap-3">
        {twinFrame.source !== 'energyplus' && (
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('aees:open-historical-player'))}
            className="hidden h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-full bg-[#f1f3f4] px-4 text-xs font-medium text-[#555b63] transition-colors hover:bg-[#e7e9eb] hover:text-[#171a20] md:flex"
          >
            <i className="fa-solid fa-clock-rotate-left" />
            시점 변경
          </button>
        )}
        <div className="hidden items-center gap-3 border-r border-[#e1e4e7] pr-4 sm:flex">
          <div className={`h-2 w-2 rounded-full ${twinFrame.source === 'unavailable' ? 'bg-[#b33f51]' : 'bg-[#22a06b]'}`} />
          <div className="text-right">
            <p className="text-xs font-semibold text-[#25292e]">
              {twinFrame.date && twinFrame.time
                ? `${twinFrame.date} ${twinFrame.time.slice(0, 5)}`
                : time.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
            </p>
            <p className="mt-0.5 text-[10px] text-[#777d85]">{sourceText(twinFrame)}</p>
          </div>
        </div>

        <ControlSetpointStrip className="hidden min-[1800px]:flex" />

        <div className="hidden items-center gap-2 border-r border-[#e1e4e7] pr-4 lg:flex">
          <div className="text-right">
            <p className="text-[10px] font-semibold text-[#777d85]">{isEnergyPlusClosedLoop ? '테스트 제어' : '운영 모드'}</p>
            <p className="mt-0.5 text-[10px] font-semibold text-[#08777d]">
              {isEnergyPlusClosedLoop
                ? controlMode === ControlMode.DECISION_SUPPORT
                  ? '범비 승인 대기 · 기존값 유지 안전조건'
                  : controlMode === ControlMode.MANUAL
                    ? '운영자 설정값 · 다음 스텝 적용'
                    : energyPlusControlDetail
                : controlMode === ControlMode.DECISION_SUPPORT ? '미승인 시 기존값 유지' : controlMode}
            </p>
          </div>
          {renderControlModeButtons()}
        </div>

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => {
              setMenuOpen(open => !open);
              setConfirming(false);
            }}
            className="group flex items-center gap-3 border-l border-[#e1e4e7] pl-4"
          >
            <div className="hidden text-right lg:block">
              <p className="text-xs font-medium text-[#25292e] transition-colors group-hover:text-[#08777d]">
                {user?.name ?? 'Unknown'}
              </p>
              <p className={`mt-0.5 text-[8px] ${roleColor}`}>{roleLabel}</p>
            </div>
            <div className={`flex h-9 w-9 items-center justify-center rounded-full border bg-[#f2f4f5] transition-all ${
              menuOpen ? 'border-[#8bc7c4] text-[#08777d]' : 'border-[#dce0e3] text-[#646a72] group-hover:border-[#aeb4ba] group-hover:text-[#171a20]'
            }`}>
              <i className={`fa-solid ${roleIcon} text-sm`} />
            </div>
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-12 z-50 w-64 overflow-hidden rounded-2xl border border-[#dfe3e6] bg-white shadow-[0_18px_50px_rgba(17,24,39,0.14)] animate-in fade-in slide-in-from-top-2 duration-150">
              <div className="border-b border-[#eceeef] px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className={`flex h-9 w-9 items-center justify-center rounded-xl border ${
                    user?.role === 'admin'
                      ? 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400'
                      : 'border-violet-500/30 bg-violet-500/10 text-violet-400'
                  }`}>
                    <i className={`fa-solid ${roleIcon} text-sm`} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#171a20]">{user?.name}</p>
                    <p className={`text-[10px] font-mono ${roleColor}`}>@{user?.username} · {roleLabel}</p>
                  </div>
                </div>
              </div>

              <div className="border-b border-[#eceeef] px-4 py-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[#6b7179]">세션 상태</span>
                  <div className="flex items-center gap-1.5">
                    <div className="h-1.5 w-1.5 rounded-full bg-[#22a06b]" />
                    <span className="text-[10px] text-[#167452]">활성</span>
                  </div>
                </div>
              </div>

              <div className="p-2">
                <button
                  onClick={handleLogoutClick}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all ${
                    confirming
                      ? 'border border-rose-500/40 bg-rose-500/20 text-rose-300'
                      : 'text-[#626870] hover:bg-[#f5f6f7] hover:text-[#b33f51]'
                  }`}
                >
                  <i className={`fa-solid ${confirming ? 'fa-circle-exclamation' : 'fa-right-from-bracket'} text-sm`} />
                  <div>
                    <p className="text-xs font-orbitron font-bold uppercase tracking-widest">
                      {confirming ? '한 번 더 클릭하면 로그아웃' : '로그아웃'}
                    </p>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
